import { FC, PropsWithChildren, useMemo, useEffect } from "react";

// Redux
import { useGetProfileQuery, useGetRolesQuery } from "@/redux/user/user.api";

// Components
import Sidebar from "@/components/Sidebar/Sidebar";
import { SplashScreen } from "@/components/Loader/SplashScreen";
import Chatbot from "@/components/Chatbot/Chatbot";
import { WebsocketManager } from "@/managers/WebsocketManager";
import { cn } from "@fido/ui-lib";
import { useMatches } from "react-router";
import { ROUTE_PORTAL_NAME } from "@/constants/Route.constant";
import { Header } from "@/components/Header/Header";
import { useLayoutRouteFlags } from "@/hooks/layout/useLayoutRouteFlags";
import { useUserProfile } from "@/hooks/query/useUserProfile";
import { useSelector } from "react-redux";
import { userAuthorizedSelector } from "@/redux/authorization/authorization.selector";

export const AppLayout: FC<PropsWithChildren> = ({ children }) => {
  const { isAuthorized } = useSelector(userAuthorizedSelector);
  const { isLoading: isProfileLoading } = useUserProfile();
  const { isLoading: isRolesLoading } = useGetRolesQuery(undefined, {
    skip: !isAuthorized
  });

  const matches = useMatches();
  const { isHomeRoute, isBrowseRoute, isProfileRoute, isFullWidthPage } =
    useLayoutRouteFlags();

  // Handle page titles from route handles
  useEffect(() => {
    const currentMatch = matches[matches.length - 1];
    const title = (currentMatch?.handle as { title?: string })?.title;

    if (title) {
      document.title = `${ROUTE_PORTAL_NAME} - ${title}`;
    }
  }, [matches]);

  const bgColor = useMemo(() => {
    if (isHomeRoute || isProfileRoute || isBrowseRoute) {
      return "linear-gradient(235.7deg, rgba(214, 243, 255, 0.2) 0%, rgba(113, 197, 232, 0.2) 71.05%), #FFFFFF";
    }

    return "#f9f9f9";
  }, [isHomeRoute, isProfileRoute, isBrowseRoute]);

  return (
    <div
      className={cn("flex")}
      style={{ background: bgColor }}
    >
      <SplashScreen active={isProfileLoading || isRolesLoading} />
      <Sidebar />
      <div
        className={cn(
          "relative flex flex-col transition-all duration-300 ease-in-out left-0 w-full md:left-[250px] md:w-[calc(100%-250px)]"
        )}
      >
        <Header />
        <div
          className={cn(
            "flex min-h-[calc(100vh-var(--header-height))] flex-col py-5",
            isFullWidthPage ? "w-full px-8" : "container"
          )}
        >
          {children}
        </div>
      </div>

      <Chatbot />

      <WebsocketManager />
    </div>
  );
};
